"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  validateApplePaySession,
  authorizeApplePayWithVgsProxy,
} from "@/networking/fast-checkout/apple-pay-vault.api";
import { CartResponse, updateCartAttributes } from "@/networking/carts.api";
import { checkoutCart } from "@/networking/checkout.api";
import { updateAddress, type Address } from "@/networking/addresses.api";
import { updateShippingMethod } from "@/networking/shipping.api";
import { buildRecurringPaymentRequestFromCart } from "@/lib/apple-pay-recurring";
import { toPaymentAmount } from "@/lib/utils";
import { checkoutRedirect } from "@/lib/checkout-redirect";
import { isApiError } from "@/lib/api-helpers";

/* ---------- helpers ---------- */
interface UseAppleVaultCheckoutProps {
  onSuccess?: () => Promise<void>;
  onError?: (error: Error) => void;
  onRedirect?: (url: string) => void;
  paymentMethodsIncludes?: string | null;
  deviceData?: string;
  kountSessionId?: string;
}

type CartQueryData = { cart: CartResponse["cart"] };

type ApiShippingMethod =
  CartResponse["cart"]["available_shipping_methods"] extends Array<infer T>
    ? T
    : never;

function toApplePayShippingMethod(
  m: ApiShippingMethod,
  amount: (s: string | number) => string,
): ApplePayJS.ApplePayShippingMethod {
  const detail =
    typeof (m as { delivery_time_estimate?: string }).delivery_time_estimate ===
    "string"
      ? (m as { delivery_time_estimate: string }).delivery_time_estimate
      : "";
  return {
    label: (m as { title: string }).title,
    amount: amount(String((m as { price: number }).price)),
    detail,
    identifier: String((m as { id: string | number }).id),
  };
}

type ApplePayPRMerchantSession = object;
interface ApplePayMerchantValidationEvent {
  validationURL: string;
  complete: (
    merchantSession: ApplePayPRMerchantSession | null,
  ) => Promise<void> | void;
}

type ApplePayPRRequest = PaymentRequest & {
  onmerchantvalidation?: (e: ApplePayMerchantValidationEvent) => void;
};

const APPLE_PAY_DEV_OFF =
  process.env.NEXT_PUBLIC_HIDE_APPLE_PAY_IN_DEV === "true";

const isSecureAppleContext = () => {
  if (typeof window === "undefined") return false;
  if (APPLE_PAY_DEV_OFF) return false;
  return window.isSecureContext;
};

// Returns the Apple Pay-capable payment method for this cart. Prefers a
// CreditCard PA with apple_pay enabled; falls back to a PayPal PA flagged for
// decrypted Apple Pay (backend routes via that PA and VGS dispatches to PayPal
// instead of the card PSP).
type ApplePayMethod = CartResponse["cart"]["available_payment_methods"][number];

const findApplePayMethod = (
  cart: CartResponse["cart"],
): ApplePayMethod | undefined => {
  const creditCard = cart.available_payment_methods?.find(
    (m) => m.type === "credit_card" && m.apple_pay === true,
  );
  if (creditCard) return creditCard;

  return cart.available_payment_methods?.find(
    (m) => m.type === "paypal" && m.decrypted_apple_pay_via_paypal === true,
  );
};

// Build the Error to surface for Apple Pay session-validation failures.
//
// fetchApi throws a plain `apiErrorSchema`-shaped object (not an Error
// instance), so for those throws we wrap the API's friendly
// `error_message` into a new Error for the customer. For native JS
// Errors (network TypeError, AbortError, programming errors), we
// pass the original through unchanged so debuggers and Sentry keep
// the full stack and type.
const errorToThrow = (e: unknown): Error => {
  if (isApiError(e)) {
    const message =
      typeof e.data?.error_message === "string"
        ? e.data.error_message
        : // fetchApi already extracted the best available message
          // (from error_message, error, or message fields) into e.message.
          e.message;
    return new Error(message);
  }
  if (e instanceof Error) {
    return e;
  }
  return new Error("Apple Pay validation failed");
};

const derivePaymentType = (
  cart: CartResponse["cart"],
): "paypal" | undefined => {
  const pm = findApplePayMethod(cart);
  return pm?.type === "paypal" ? "paypal" : undefined;
};

/* ---------- hook ---------- */
export const useAppleVaultCheckout = ({
  onSuccess,
  onError,
  onRedirect,
  paymentMethodsIncludes,
  deviceData,
  kountSessionId,
}: UseAppleVaultCheckoutProps = {}) => {
  const queryClient = useQueryClient();

  const [applePaySupported, setApplePaySupported] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [supportsQRCode, setSupportsQRCode] = useState(false);
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState<
    string | null
  >(null);

  const isStartingRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      setApplePaySupported(false);
      setSupportsQRCode(false);
      return;
    }

    if (!isSecureAppleContext()) {
      setApplePaySupported(false);
      setSupportsQRCode(false);
      return;
    }

    const S = window.ApplePaySession as typeof ApplePaySession | undefined;
    const safari =
      !!S && (typeof S.supportsVersion !== "function" || S.supportsVersion(15));
    const prApplePay = "PaymentRequest" in window;

    setApplePaySupported(safari || prApplePay);

    try {
      // canMakePayments returns boolean, but some implementations may differ
      const maybe: unknown = S?.canMakePayments?.();
      if (maybe && typeof (maybe as { then?: unknown }).then === "function") {
        (maybe as Promise<boolean>)
          .then((v) => setSupportsQRCode(!v))
          .catch(() => setSupportsQRCode(true));
      } else {
        setSupportsQRCode(!maybe);
      }
    } catch {
      setSupportsQRCode(true);
    }
  }, []);

  const getEffectiveTotal = useCallback(
    (cart: CartQueryData["cart"]): number => {
      if (cart.points_applied && cart.points_applied > 0) {
        return cart.remaining_cart_amount_after_points ?? cart.amount_total;
      }
      return cart.amount_total;
    },
    [],
  );

  /** Write contacts (email/phone/bill_to/ship_to) to cart and optionally recompute totals */
  const writeContactsToCart = useCallback(
    async ({
      cartToken,
      email,
      phone,
      billTo,
      shipTo,
      recomputeTotals,
    }: {
      cartToken: string;
      email?: string | null;
      phone?: string | null;
      billTo?: ApplePayJS.ApplePayPaymentContact | null;
      shipTo?: ApplePayJS.ApplePayPaymentContact | null;
      recomputeTotals: boolean;
    }): Promise<CartQueryData> => {
      const ops: Promise<unknown>[] = [];

      if (email || phone) {
        ops.push(
          updateCartAttributes({
            cartToken,
            ...(email ? { email } : {}),
            ...(phone ? { phone } : {}),
          }).catch(() => {}),
        );
      }

      const toAddr = (
        c: ApplePayJS.ApplePayPaymentContact,
      ): Address["address"] => ({
        name: [c.givenName?.trim(), c.familyName?.trim()]
          .filter(Boolean)
          .join(" "),
        address1: c.addressLines?.[0] ?? "",
        address2: c.addressLines?.[1] ?? "",
        city: c.locality ?? "",
        state: c.administrativeArea ?? "",
        postal_code: c.postalCode ?? "",
        country_code: c.countryCode ?? "US",
      });

      // Helper to check if we have enough location data for shipping/tax calculation
      const hasUsefulLocationData = (
        contact: ApplePayJS.ApplePayPaymentContact,
      ): boolean => {
        return !!(
          contact.countryCode ||
          contact.postalCode ||
          (contact.administrativeArea && contact.locality)
        );
      };

      if (billTo?.addressLines?.length) {
        ops.push(
          updateAddress(
            { type: "bill_to", address: toAddr(billTo) },
            cartToken,
          ).catch(() => {}),
        );
      } else if (billTo && hasUsefulLocationData(billTo)) {
        ops.push(
          updateAddress(
            { type: "bill_to", address: toAddr(billTo) },
            cartToken,
          ).catch(() => {}),
        );
      }

      if (shipTo?.addressLines?.length) {
        ops.push(
          updateAddress(
            { type: "ship_to", address: toAddr(shipTo) },
            cartToken,
          ).catch(() => {}),
        );
      } else if (shipTo && hasUsefulLocationData(shipTo)) {
        ops.push(
          updateAddress(
            { type: "ship_to", address: toAddr(shipTo) },
            cartToken,
          ).catch(() => {}),
        );
      }

      await Promise.all(ops);

      const prev = queryClient.getQueryData([
        "cart",
        cartToken,
        paymentMethodsIncludes ?? null,
      ]) as CartQueryData | undefined;
      if (prev?.cart && email) {
        queryClient.setQueryData(
          ["cart", cartToken, paymentMethodsIncludes ?? null],
          {
            cart: { ...prev.cart, email },
          } as CartQueryData,
        );
      }

      if (!recomputeTotals) {
        const local =
          (queryClient.getQueryData([
            "cart",
            cartToken,
            paymentMethodsIncludes ?? null,
          ]) as CartQueryData | undefined) ??
          ((await queryClient.fetchQuery({
            queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
          })) as CartQueryData);
        return local;
      }

      // Recompute totals with new address data
      await queryClient.invalidateQueries({
        queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
      });
      return (await queryClient.fetchQuery({
        queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
      })) as CartQueryData;
    },
    [queryClient, paymentMethodsIncludes],
  );

  const startPrApplePay = useCallback(
    async (cartTokenLocal: string, cartLocal: CartQueryData["cart"]) => {
      if (!isSecureAppleContext()) {
        throw new Error("Apple Pay isn’t available on this page.");
      }
      // 1) Ensure the cart exposes an Apple Pay merchant
      const pm = findApplePayMethod(cartLocal);
      const merchantIdentifier = pm?.apple_pay_merchant_identifier;
      if (!merchantIdentifier) {
        throw new Error("Apple Pay merchant not available on this cart");
      }

      const hasSubscription =
        cartLocal.items?.some((i) => i.subscription || i.subscription_only) ===
        true;

      // 2) Build Payment Request
      const methodData = [
        {
          supportedMethods: "https://apple.com/apple-pay",
          data: {
            merchantIdentifier,
            countryCode: cartLocal.country?.iso || "US",
            currencyCode: cartLocal.currency_code,
            supportedNetworks: ["visa", "masterCard", "amex", "discover"],
            merchantCapabilities: ["supports3DS"],
            ...(hasSubscription
              ? {
                  recurringPaymentRequest: buildRecurringPaymentRequestFromCart(
                    cartLocal,
                    cartLocal.currency_decimal_places ?? 2,
                  ),
                }
              : {}),
          },
        },
      ];

      const details = {
        displayItems: [
          {
            label: "Subtotal",
            amount: {
              currency: cartLocal.currency_code,
              value: toPaymentAmount(
                cartLocal.sub_total,
                cartLocal.currency_decimal_places,
              ),
            },
          },
          {
            label: "Shipping",
            amount: {
              currency: cartLocal.currency_code,
              value: toPaymentAmount(
                cartLocal.shipping_total,
                cartLocal.currency_decimal_places,
              ),
            },
          },
          {
            label: "Tax",
            amount: {
              currency: cartLocal.currency_code,
              value: toPaymentAmount(
                cartLocal.tax_total,
                cartLocal.currency_decimal_places,
              ),
            },
          },
        ],
        total: {
          label: cartLocal.company?.name || "Total",
          amount: {
            currency: cartLocal.currency_code,
            value: toPaymentAmount(
              getEffectiveTotal(cartLocal),
              cartLocal.currency_decimal_places,
            ),
          },
        },
      };

      const req: ApplePayPRRequest = new PaymentRequest(methodData, details);

      // 3) Merchant validation (Apple extension to Payment Request)
      req.onmerchantvalidation = async (e: ApplePayMerchantValidationEvent) => {
        try {
          const res = await validateApplePaySession(
            e.validationURL,
            cartTokenLocal,
            cartLocal.company?.id,
            cartLocal.country?.id,
          );
          const sessionData =
            typeof res.apple_pay === "string"
              ? JSON.parse(res.apple_pay)
              : (res.apple_pay ?? (res.body ? JSON.parse(res.body) : null));
          await e.complete(sessionData as ApplePayPRMerchantSession);
        } catch (err) {
          try {
            await e.complete(null);
          } catch {
            /* noop */
          }
          throw errorToThrow(err);
        }
      };

      // 4) Show Apple UI (Chrome/Firefox → QR to continue on iPhone)
      const resp: PaymentResponse = await req.show();

      try {
        // 5) Optionally persist contacts if present on the response
        type ApplePayPRDetails = {
          shippingContact?: ApplePayJS.ApplePayPaymentContact;
          billingContact?: ApplePayJS.ApplePayPaymentContact;
          payerEmail?: string;
          token?: ApplePayJS.ApplePayPaymentToken;
        };
        const applePayDetails: ApplePayPRDetails = (resp.details ||
          {}) as ApplePayPRDetails;
        const shippingContact = applePayDetails.shippingContact;
        const billingContact = applePayDetails.billingContact;
        const payerEmail =
          applePayDetails.payerEmail ||
          shippingContact?.emailAddress ||
          billingContact?.emailAddress ||
          null;
        const phone =
          shippingContact?.phoneNumber ?? billingContact?.phoneNumber ?? null;

        try {
          await writeContactsToCart({
            cartToken: cartTokenLocal,
            email: payerEmail,
            phone,
            billTo: billingContact ?? null,
            shipTo: shippingContact ?? null,
            recomputeTotals: true,
          });
          await queryClient.invalidateQueries({
            queryKey: ["cart", cartTokenLocal, paymentMethodsIncludes ?? null],
          });
        } catch {
          // continue even if contact write fails
        }

        // 6) Send token to VGS (same as Safari path)
        const fresh = (await queryClient.fetchQuery({
          queryKey: ["cart", cartTokenLocal, paymentMethodsIncludes ?? null],
        })) as CartQueryData;

        if (fresh.cart.valid_for_checkout_pre_payment === false) {
          await resp.complete("fail");
          throw new Error("Cart not ready for checkout. Please try again.");
        }

        const prPayload = { details: applePayDetails };
        await authorizeApplePayWithVgsProxy(
          cartTokenLocal,
          prPayload,
          deviceData,
          derivePaymentType(fresh.cart),
        );

        await resp.complete("success");

        // 7) Checkout
        const result = await checkoutCart(cartTokenLocal, {
          deviceData,
          kountSessionId,
        });
        if (onSuccess) await onSuccess();

        checkoutRedirect(result, cartTokenLocal);
      } catch (err) {
        try {
          await resp.complete("fail");
        } catch {
          /* noop */
        }
        throw err;
      }
    },
    [
      getEffectiveTotal,
      queryClient,
      paymentMethodsIncludes,
      onSuccess,
      onRedirect,
      writeContactsToCart,
      deviceData,
      kountSessionId,
    ],
  );

  const handleAppleVaultCheckout = useCallback(
    async (cartToken: string) => {
      if (!isSecureAppleContext()) {
        onError?.(new Error("Apple Pay isn’t available on this page."));
        return;
      }
      if (isStartingRef.current) return;
      isStartingRef.current = true;
      setIsProcessing(true);

      try {
        const cached = queryClient.getQueryData([
          "cart",
          cartToken,
          paymentMethodsIncludes ?? null,
        ]) as CartQueryData | undefined;
        let cart = cached?.cart;
        if (!cart) throw new Error("No cart data available");

        // Determine runtime environment

        const ApplePaySessionCtor = window.ApplePaySession;
        const canUsePaymentRequest = "PaymentRequest" in window;

        if (!ApplePaySessionCtor && canUsePaymentRequest) {
          await startPrApplePay(cartToken, cart);
          return;
        }

        // Safari path (ApplePaySession)
        if (!ApplePaySessionCtor) throw new Error("Apple Pay not available");

        const isDigitalOnly = cart.digital_only === true;

        const availableShippingMethods =
          cart.available_shipping_methods?.slice() ?? [];

        if (!isDigitalOnly && availableShippingMethods.length > 0) {
          let chosenId =
            selectedShippingMethodId ?? cart.shipping_method?.id?.toString();

          if (!chosenId && availableShippingMethods[0]) {
            chosenId = String(availableShippingMethods[0].id);
            setSelectedShippingMethodId(chosenId);
            await updateShippingMethod({
              cartToken,
              shippingMethodId: availableShippingMethods[0].id,
            });
            // Refresh cart after setting shipping method
            await queryClient.invalidateQueries({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            });
            const updatedCartData = (await queryClient.fetchQuery({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            })) as CartQueryData;
            cart = updatedCartData.cart;
          }
        }

        const shippingMethods = isDigitalOnly
          ? []
          : (cart.available_shipping_methods ?? []).map((m) =>
              toApplePayShippingMethod(
                m as ApiShippingMethod,
                (v: string | number) =>
                  toPaymentAmount(
                    typeof v === "number" ? v : parseFloat(String(v ?? "0")),
                    cart.currency_decimal_places,
                  ),
              ),
            );

        const request: ApplePayJS.ApplePayPaymentRequest = {
          countryCode: cart.country?.iso || "US",
          currencyCode: cart.currency_code,
          supportedNetworks: ["visa", "masterCard", "amex", "discover"],
          merchantCapabilities: ["supports3DS"],
          requiredBillingContactFields: [
            "postalAddress",
            "name",
            "email",
            "phone",
          ],
          requiredShippingContactFields: isDigitalOnly
            ? ["email", "phone"]
            : ["postalAddress", "name", "email", "phone"],
          shippingMethods: shippingMethods,
          shippingType: isDigitalOnly ? undefined : "shipping",
          lineItems: [
            {
              label: "Subtotal",
              amount: toPaymentAmount(
                cart.sub_total,
                cart.currency_decimal_places,
              ),
              type: "final",
            },
            {
              label: "Shipping",
              amount: toPaymentAmount(
                cart.shipping_total,
                cart.currency_decimal_places,
              ),
              type: "final",
            },
            {
              label: "Tax",
              amount: toPaymentAmount(
                cart.tax_total,
                cart.currency_decimal_places,
              ),
              type: "final",
            },
          ],
          total: {
            label: cart.company?.name || "Total",
            amount: toPaymentAmount(
              getEffectiveTotal(cart),
              cart.currency_decimal_places,
            ),
            type: "final",
          },
        };

        // If the cart contains a subscription, attach the recurring request
        const hasSubscription =
          cart.items?.some((i) => i.subscription || i.subscription_only) ===
          true;

        if (hasSubscription) {
          const recurringRequest = buildRecurringPaymentRequestFromCart(
            cart,
            cart.currency_decimal_places ?? 2,
          );

          (
            request as ApplePayJS.ApplePayPaymentRequest & {
              recurringPaymentRequest?: ApplePayJS.ApplePayRecurringPaymentRequest;
            }
          ).recurringPaymentRequest = recurringRequest;
        }

        const session = new ApplePaySessionCtor(15, request);

        session.onvalidatemerchant = async (
          evt: ApplePayJS.ApplePayValidateMerchantEvent,
        ) => {
          try {
            const res = await validateApplePaySession(
              evt.validationURL,
              cartToken,
              cart.company?.id,
              cart.country?.id,
            );
            const sessionData =
              typeof res.apple_pay === "string"
                ? JSON.parse(res.apple_pay)
                : (res.apple_pay ?? (res.body ? JSON.parse(res.body) : null));
            session.completeMerchantValidation(sessionData as unknown);
          } catch (e) {
            try {
              session.abort();
            } catch {
              /* noop */
            }
            onError?.(errorToThrow(e));
          }
        };

        session.onshippingcontactselected = async (
          e: ApplePayJS.ApplePayShippingContactSelectedEvent,
        ) => {
          try {
            const updated = await writeContactsToCart({
              cartToken,
              email: e.shippingContact.emailAddress ?? null,
              phone: e.shippingContact.phoneNumber ?? null,
              billTo: null, // wait for payment authorization
              shipTo: e.shippingContact,
              recomputeTotals: !isDigitalOnly,
            });

            const available =
              updated.cart.available_shipping_methods?.slice() ?? [];

            if (!isDigitalOnly && available.length === 0) {
              session.completeShippingContactSelection(
                ApplePaySessionCtor.STATUS_FAILURE,
                [],
                {
                  label: "No shipping available",
                  amount: toPaymentAmount(
                    getEffectiveTotal(cart),
                    cart.currency_decimal_places,
                  ),
                  type: "final",
                },
                [],
              );
              return;
            }

            // Auto-select first shipping method if none selected
            let chosenId =
              selectedShippingMethodId ??
              updated.cart.shipping_method?.id?.toString();

            if (
              !isDigitalOnly &&
              !chosenId &&
              available.length > 0 &&
              available[0]
            ) {
              chosenId = String(available[0].id);
              setSelectedShippingMethodId(chosenId);
              await updateShippingMethod({
                cartToken,
                shippingMethodId: available[0].id,
              });
              await queryClient.invalidateQueries({
                queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
              });
            }

            const finalCart = (await queryClient.fetchQuery({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            })) as CartQueryData;

            session.completeShippingContactSelection(
              ApplePaySessionCtor.STATUS_SUCCESS,
              isDigitalOnly
                ? []
                : (finalCart.cart.available_shipping_methods ?? []).map((m) =>
                    toApplePayShippingMethod(
                      m as ApiShippingMethod,
                      (v: string | number) =>
                        toPaymentAmount(
                          typeof v === "number"
                            ? v
                            : parseFloat(String(v ?? "0")),
                          finalCart.cart.currency_decimal_places,
                        ),
                    ),
                  ),
              {
                label: cart.company?.name || "Total",
                amount: toPaymentAmount(
                  getEffectiveTotal(finalCart.cart),
                  finalCart.cart.currency_decimal_places,
                ),
                type: "final",
              },
              [
                {
                  label: "Subtotal",
                  amount: toPaymentAmount(
                    finalCart.cart.sub_total,
                    finalCart.cart.currency_decimal_places,
                  ),
                  type: "final",
                },
                {
                  label: "Shipping",
                  amount: toPaymentAmount(
                    finalCart.cart.shipping_total,
                    finalCart.cart.currency_decimal_places,
                  ),
                  type: "final",
                },
                {
                  label: "Tax",
                  amount: toPaymentAmount(
                    finalCart.cart.tax_total,
                    finalCart.cart.currency_decimal_places,
                  ),
                  type: "final",
                },
              ],
            );
          } catch (err) {
            session.completeShippingContactSelection(
              ApplePaySessionCtor.STATUS_FAILURE,
              [],
              {
                label: "Error",
                amount: toPaymentAmount(
                  getEffectiveTotal(cart),
                  cart.currency_decimal_places,
                ),
                type: "final",
              },
              [],
            );
            onError?.(
              err instanceof Error
                ? err
                : new Error("Shipping selection failed"),
            );
          }
        };

        session.onshippingmethodselected = async (
          e: ApplePayJS.ApplePayShippingMethodSelectedEvent,
        ) => {
          try {
            setSelectedShippingMethodId(e.shippingMethod.identifier);
            await updateShippingMethod({
              cartToken,
              shippingMethodId: e.shippingMethod.identifier,
            });
            await queryClient.invalidateQueries({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            });
            const updated = (await queryClient.fetchQuery({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            })) as CartQueryData;

            session.completeShippingMethodSelection(
              ApplePaySessionCtor.STATUS_SUCCESS,
              {
                label: cart.company?.name || "Total",
                amount: toPaymentAmount(
                  getEffectiveTotal(updated.cart),
                  updated.cart.currency_decimal_places,
                ),
                type: "final",
              },
              [
                {
                  label: "Subtotal",
                  amount: toPaymentAmount(
                    updated.cart.sub_total,
                    updated.cart.currency_decimal_places,
                  ),
                  type: "final",
                },
                {
                  label: "Shipping",
                  amount: toPaymentAmount(
                    updated.cart.shipping_total,
                    updated.cart.currency_decimal_places,
                  ),
                  type: "final",
                },
                {
                  label: "Tax",
                  amount: toPaymentAmount(
                    updated.cart.tax_total,
                    updated.cart.currency_decimal_places,
                  ),
                  type: "final",
                },
              ],
            );
          } catch {
            session.completeShippingMethodSelection(
              ApplePaySessionCtor.STATUS_FAILURE,
              {
                label: "Error",
                amount: toPaymentAmount(
                  getEffectiveTotal(cart),
                  cart.currency_decimal_places,
                ),
                type: "final",
              },
              [],
            );
          }
        };

        session.onpaymentauthorized = async (
          e: ApplePayJS.ApplePayPaymentAuthorizedEvent,
        ) => {
          try {
            await writeContactsToCart({
              cartToken,
              email:
                e.payment.shippingContact?.emailAddress ??
                e.payment.billingContact?.emailAddress ??
                null,
              phone:
                e.payment.shippingContact?.phoneNumber ??
                e.payment.billingContact?.phoneNumber ??
                null,
              billTo: e.payment.billingContact ?? null,
              shipTo: e.payment.shippingContact ?? null,
              recomputeTotals: true,
            });

            await queryClient.invalidateQueries({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            });

            const freshCart = (await queryClient.fetchQuery({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            })) as CartQueryData;

            if (freshCart.cart.valid_for_checkout_pre_payment === false) {
              session.completePayment(ApplePaySessionCtor.STATUS_FAILURE);
              onError?.(
                new Error("Cart not ready for checkout. Please try again."),
              );
              return;
            }

            // VGS Authorization
            try {
              const safariPayload = { details: { token: e.payment.token } };
              await authorizeApplePayWithVgsProxy(
                cartToken,
                safariPayload,
                deviceData,
                derivePaymentType(freshCart.cart),
              );
            } catch (vgsError) {
              session.completePayment(ApplePaySessionCtor.STATUS_FAILURE);
              const vgsMessage =
                vgsError instanceof Error
                  ? vgsError.message
                  : "Payment authorization failed";
              onError?.(new Error(vgsMessage));
              return;
            }

            session.completePayment(ApplePaySessionCtor.STATUS_SUCCESS);

            // Checkout
            try {
              const result = await checkoutCart(cartToken, {
                deviceData,
                kountSessionId,
              });
              if (onSuccess) await onSuccess();

              checkoutRedirect(result, cartToken);
            } catch (checkoutError) {
              let checkoutMessage = "Checkout failed. Please contact support.";

              if (checkoutError instanceof Error) {
                checkoutMessage = checkoutError.message;

                if (checkoutMessage.includes("Unsupported ApplePay action")) {
                  checkoutMessage =
                    "This payment method is not supported for this transaction. Please try a different payment method.";
                } else if (checkoutMessage.includes("capture")) {
                  checkoutMessage =
                    "Payment processing is temporarily unavailable. Please try again or contact support.";
                }
              }

              onError?.(new Error(checkoutMessage));
              return;
            }
          } catch (err) {
            session.completePayment(ApplePaySessionCtor.STATUS_FAILURE);
            onError?.(
              err instanceof Error ? err : new Error("Apple Pay failed"),
            );
          }
        };

        session.begin();
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error("Apple Pay failed"));
      } finally {
        isStartingRef.current = false;
        setIsProcessing(false);
      }
    },
    [
      getEffectiveTotal,
      onError,
      onRedirect,
      onSuccess,
      paymentMethodsIncludes,
      queryClient,
      selectedShippingMethodId,
      startPrApplePay,
      writeContactsToCart,
      deviceData,
      kountSessionId,
    ],
  );

  return {
    applePaySupported,
    supportsQRCode,
    isProcessing,
    handleAppleVaultCheckout,
  };
};
